// "Extra products" of a category - used for festival / offer categories.
//
// A product keeps its own category and can ALSO be shown in this one, so a
// "Dubai Shopping Festival" category can hold brushes, plates and mugs
// without taking them out of Cleaning Tools / Tableware. The shop lists
// the category's own products + these (product/productsearchbycatid).
//
// API (admin token):
//   category/extraproducts          { cat_id }               -> products
//   category/extraproducts/add      { cat_id, product_ids }
//   category/extraproducts/remove   { cat_id, product_id }
//   product/search                  { search_str }           -> picker
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';
const base = () => String(process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');

export const imageUrl = (name?: string | null) => {
  const n = String(name || '').replace(/^.*uploads[\\/]/, '');
  return n ? `${base()}/uploads/${n}` : undefined;
};

// POST with the admin token; throws the server message on failure
export async function adminPost(path: string, body: any) {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('auth_token') || '';
  const res = await fetch(`${base()}/${path.replace(/^\//, '')}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authkey: AUTH_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok || data?.error === true) {
    throw new Error(data?.message || `Request failed (${res.status})`);
  }
  return data;
}

export type PickedProduct = {
  id: number;
  prod_name: string;
  product_main_image?: string | null;
  cat_name?: string | null;
};

// Product search box (name or #id). Used here and by the banner link picker.
export function ProductPicker({
  value,
  onChange,
  label = 'Search product by name or ID',
  disabled,
  excludeIds = [],
}: {
  value: PickedProduct | null;
  onChange: (p: PickedProduct | null) => void;
  label?: string;
  disabled?: boolean;
  excludeIds?: number[];
}) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<PickedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const q = input.trim();
    if (q.length < 2 || (value && q === value.prod_name)) {
      setOptions(value ? [value] : []);
      return undefined;
    }
    const id = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await adminPost('product/search', { search_str: q, start: 0, limit: 20 });
        if (id !== seq.current) return;
        const rows: any[] = data?.produclist || data?.products || [];
        setOptions(
          rows
            .map(r => ({
              id: Number(r.id ?? r.product_id),
              prod_name: String(r.prod_name || r.product_name || ''),
              product_main_image: r.product_main_image,
              cat_name: r.cat_name,
            }))
            .filter(r => r.id && !excludeIds.includes(r.id)),
        );
      } catch {
        if (id === seq.current) setOptions([]);
      } finally {
        if (id === seq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [input]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      options={options}
      loading={loading}
      disabled={disabled}
      filterOptions={x => x}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={o => (o?.prod_name ? `${o.prod_name}  (#${o.id})` : '')}
      noOptionsText={input.trim().length < 2 ? 'Type at least 2 letters' : 'No products found'}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          <Avatar src={imageUrl(o.product_main_image)} variant="rounded" sx={{ width: 32, height: 32, mr: 1.5, bgcolor: '#f1f2f4' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>{o.prod_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              #{o.id}{o.cat_name ? ` · ${o.cat_name}` : ''}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

type ExtraRow = {
  product_id: number;
  prod_name: string;
  product_main_image?: string | null;
  own_cat_name?: string | null;
  status?: number;
};

export default function CategoryExtraProducts({ catId, catName }: { catId: number; catName?: string }) {
  const [rows, setRows] = useState<ExtraRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<PickedProduct | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const show = (message: string, severity: 'success' | 'error') => setSnack({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminPost('category/extraproducts', { cat_id: catId });
      setRows(Array.isArray(data?.products) ? data.products : []);
    } catch (e: any) {
      show(e?.message || 'Could not load the products of this category', 'error');
    } finally {
      setLoading(false);
    }
  }, [catId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await adminPost('category/extraproducts/add', { cat_id: catId, product_ids: [picked.id] });
      setPicked(null);
      show(`"${picked.prod_name}" added`, 'success');
      await load();
    } catch (e: any) {
      show(e?.message || 'Could not add the product', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: ExtraRow) => {
    if (!window.confirm(`Remove "${row.prod_name}" from ${catName || 'this category'}?\nIt stays in its own category.`)) return;
    setBusy(true);
    try {
      await adminPost('category/extraproducts/remove', { cat_id: catId, product_id: row.product_id });
      show('Product removed', 'success');
      await load();
    } catch (e: any) {
      show(e?.message || 'Could not remove the product', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add products from any category. They stay in their own category too — use this for festival
        and offer categories.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ProductPicker
            value={picked}
            onChange={setPicked}
            disabled={busy}
            excludeIds={rows.map(r => Number(r.product_id))}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={add}
          disabled={!picked || busy}
          sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: 40, '&:hover': { bgcolor: '#006e52' } }}
        >
          Add
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No extra products yet.
        </Typography>
      ) : (
        <Box sx={{ border: '1px solid #e1e3e5', borderRadius: '10px', overflow: 'hidden' }}>
          {rows.map((r, i) => (
            <Box
              key={r.product_id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderTop: i ? '1px solid #e1e3e5' : 'none',
              }}
            >
              <Avatar src={imageUrl(r.product_main_image)} variant="rounded" sx={{ width: 40, height: 40, bgcolor: '#f1f2f4', border: '1px solid #E6E1DD' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {r.prod_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  #{r.product_id}
                  {r.own_cat_name ? ` · own category: ${r.own_cat_name}` : ''}
                </Typography>
              </Box>
              {Number(r.status) === 0 && <Chip size="small" label="Inactive product" sx={{ height: 22, fontSize: 11 }} />}
              <Tooltip title="Remove from this category">
                <span>
                  <IconButton size="small" onClick={() => remove(r)} disabled={busy}>
                    <Delete fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
